import React, { useEffect } from 'react';

import { usePrevious } from '@paragon/client-tools';

import { HoveredItem } from '../../../store/slices/viewportSlice';
import { useAppSelector } from '../../hooks';
import { BearingEnvelopeTooltip } from './BearingEnvelopeTooltip';
import { CeilingPlaneTooltip } from './CeilingPlaneTooltip';
import { CeilingTrayTooltip } from './CeilingTrayTooltip';
import { TooltipTextContent } from './common';
import DesignModeTooltip from './DesignModeTooltip';
import { DimensionModeTooltip } from './DimensionModeTooltip';
import { FeatureAreaTooltip } from './FeatureAreaTooltip';
import { FeaturePatternContainerTooltip } from './FeaturePatternContainerTooltip';
import { RoofContainerTooltip } from './RoofContainerTooltip';
import { RoofPlaneTooltip } from './RoofPlaneTooltip';
import { TooltipBase, useTooltipContext } from './TooltipBase';
import { TrussEnvelopeTooltip } from './TrussEnvelopeTooltip';
import { WorkLineTooltip } from './WorkLineTooltip';

export function SceneTooltip() {
  const hoveredItem = useAppSelector((state) => state.viewport.hoveredItem);
  const inDimensionMode = useAppSelector((state) => state.application.inDimensionMode);
  const sceneTooltipOverride = useAppSelector((state) => state.viewport.sceneTooltipOverride);
  const tooltipHidden = useAppSelector((state) => state.viewport.tooltipHidden);

  if (tooltipHidden) return null;

  if (sceneTooltipOverride != null) {
    return (
      <TooltipBase>
        <TooltipTextContent text={sceneTooltipOverride} />
      </TooltipBase>
    );
  }

  if (inDimensionMode) {
    return <DimensionModeTooltip />;
  }

  if (hoveredItem) {
    return (
      // the key prevents the tooltip base from being reused (and grabbing the old tooltip's position) when the
      //   hovered item changes
      <TooltipBase key={hoveredItem.guid}>
        <SceneItemTooltip hoveredItem={hoveredItem} />
      </TooltipBase>
    );
  }

  return null;
}

function SceneItemTooltip(props: { hoveredItem: HoveredItem }) {
  const hoveredItem = useAppSelector((state) => state.viewport.hoveredItem);

  const { flushPositionChanges } = useTooltipContext();

  const currentHoveredItemGuid = props.hoveredItem.guid;
  const previousHoveredItemGuid = usePrevious(currentHoveredItemGuid);

  useEffect(() => {
    if (currentHoveredItemGuid !== previousHoveredItemGuid) {
      flushPositionChanges();
    }
  }, [currentHoveredItemGuid, previousHoveredItemGuid, flushPositionChanges]);

  switch (hoveredItem?.type) {
    case undefined:
      return null;

    case 'beam':
    case 'wall':
      return <BearingEnvelopeTooltip guid={hoveredItem.guid} />;

    case 'ceilingPlane':
      return <CeilingPlaneTooltip guid={hoveredItem.guid} />;

    case 'ceilingTray':
      return <CeilingTrayTooltip guid={hoveredItem.guid} />;

    case 'featureArea':
      return <FeatureAreaTooltip guid={hoveredItem.guid} />;

    case 'featurePatternContainer':
      return <FeaturePatternContainerTooltip guid={hoveredItem.guid} />;

    case 'roofContainer':
      return <RoofContainerTooltip guid={hoveredItem.guid} />;

    case 'roofPlane':
      return <RoofPlaneTooltip guid={hoveredItem.guid} />;

    case 'trussEnvelope':
      return <TrussEnvelopeTooltip guid={hoveredItem.guid} />;

    case 'workLine':
      return <WorkLineTooltip guid={hoveredItem.guid} />;

    default:
      return <DesignModeTooltip />;
  }
}
